import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { ShowListing, ShowListingProps } from '../../DirectoryListings/ShowListing/ShowListing';
import { DateSubheader } from '../../DateSubheader';
import { ChevronRightIcon } from '../../icons';
import ProfileSection from './ProfileSection';
import AllShowsDrawer from './AllShowsDrawer';
import '../Profile.css';

export interface ShowWithId extends Omit<ShowListingProps, 'onClick'> {
  id?: string;
}

export interface ShowGroup {
  label: string;
  pillLabel?: string;
  pillColor?: 'red' | 'green' | 'blue';
  shows: ShowWithId[];
}

export interface ShowWithRawDate extends ShowWithId {
  rawDate?: string;
}

export interface UpcomingShowsSectionProps {
  shows?: ShowWithId[];
  showGroups?: ShowGroup[];
  showAddButton?: boolean;
  addShowIcon?: React.ReactNode;
  onAddShow?: () => void;
  title?: string;
  /** All shows (with rawDate) for the View All drawer to filter upcoming/past */
  allShows?: ShowWithRawDate[];
  /** Maximum number of shows to display before showing a "See More" link (default: 3) */
  maxShows?: number;
}

/** Truncates show groups to a combined total of `max` shows, dropping empty trailing groups. */
const sliceShowGroups = (groups: ShowGroup[], max: number): ShowGroup[] => {
  let remaining = max;
  const result: ShowGroup[] = [];
  for (const group of groups) {
    if (remaining <= 0) break;
    const groupShows = group.shows.slice(0, remaining);
    if (groupShows.length > 0) {
      result.push({ ...group, shows: groupShows });
      remaining -= groupShows.length;
    }
  }
  return result;
};

export const UpcomingShowsSection: React.FC<UpcomingShowsSectionProps> = ({
  shows,
  showGroups,
  showAddButton = false,
  addShowIcon,
  onAddShow,
  title = 'Upcoming Shows',
  allShows,
  maxShows = 3,
}) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleShowClick = (showId?: string) => {
    if (showId) {
      navigate(`/shows/${showId}`);
    }
  };

  const renderShows = (showList: ShowWithId[]) => (
    <div className="profile-shows__list">
      {showList.map((show, i) => (
        <ShowListing key={show.id || i} {...show} onClick={() => handleShowClick(show.id)} />
      ))}
    </div>
  );

  const totalShowsCount = showGroups
    ? showGroups.reduce((sum, group) => sum + group.shows.length, 0)
    : shows?.length ?? 0;
  const hasMoreShows = totalShowsCount > maxShows;

  const displayShows = shows?.slice(0, maxShows);
  const displayShowGroups = showGroups ? sliceShowGroups(showGroups, maxShows) : undefined;

  return (
    <ProfileSection
      title={title}
      rightAction={<Button size="small" onClick={() => setDrawerOpen(true)}>View All</Button>}
    >
      {displayShowGroups ? (
        <div className="profile-shows__grouped">
          {displayShowGroups.map((group, i) => (
            <div key={i} className="profile-shows__group">
              <DateSubheader
                dateLabel={group.label}
                pillLabel={group.pillLabel}
                pillColor={group.pillColor}
                showNavArrows={false}
                dateClickable={false}
              />
              {renderShows(group.shows)}
            </div>
          ))}
        </div>
      ) : displayShows ? (
        renderShows(displayShows)
      ) : null}

      {hasMoreShows && (
        <div className="profile-shows__see-more-row">
          <Button size="small" onClick={() => setDrawerOpen(true)}>
            See More
            <ChevronRightIcon size="m" tone="white" />
          </Button>
        </div>
      )}

      {showAddButton && onAddShow && (
        <div className="profile-shows__add">
          <Button type="primary" shape="round" onClick={onAddShow}>
            {addShowIcon} Add Show
          </Button>
        </div>
      )}

      <AllShowsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        shows={shows}
        showGroups={showGroups}
        allShows={allShows}
      />
    </ProfileSection>
  );
};

export default UpcomingShowsSection;
